import { Stack, Typography } from '@chronosphereio/chrono-ui';
import React from 'react';
import { ExternalPanelPropsBase } from '@/core';
import { BasePanelProps } from '@/plugin-system';
import { useInterpolateContextLinkFn } from '@/plugin-system/runtime/context-links';

/**
 * Types for the external panel that are only used in the `Viz` variant.
 *
 * @see {@link ExternalPanelPropsBase} for types defined in the schema because they are also shared with
 * the `DashboardPanel` variant.
 */
interface ExternalPropsVizOnly {
  // Currently no Viz only props, but leaving this here, so it is clear where to put them in the future.
}

export interface ExternalProps extends BasePanelProps, ExternalPanelPropsBase, ExternalPropsVizOnly {
  // Extend values from `ExternalPanelPropsBase` below, as needed, to add in jsdoc to help developers.
}

const EmptyState = ({ children }: { children: React.ReactNode }) => (
  <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
    <Typography variant="body2">{children}</Typography>
  </Stack>
);

export function ExternalPanel(props: ExternalProps) {
  const { url, width, height, contentWidth, contentHeight } = props;
  const interpolateContextLink = useInterpolateContextLinkFn();

  if (width === undefined || height === undefined) return null;

  if (url === undefined || url === '') {
    return <EmptyState>No URL configured.</EmptyState>;
  }

  return (
    <iframe
      src={interpolateContextLink(url)}
      width={contentWidth === undefined ? width : `${contentWidth}px`}
      height={contentHeight === undefined ? height : `${contentHeight}px`}
      style={{
        border: 'none',
      }}
      title="Embedded content"
      // allow-scripts: lets the iframe execute JavaScript
      // allow-forms: lets the iframe submit forms
      // allow-popups: lets the iframe open new windows (e.g. window.open, target="_blank")
      // allow-same-origin: lets the iframe access its own cookies/storage (needed for authenticated embeds)
      sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
    />
  );
}
